import { Box, HStack, useRecipe } from '@chakra-ui/react';
import { motion, useReducedMotion } from 'framer-motion';
import { carouselDotsRecipe } from '../../theme/recipes/carousel-dots';

const DOT_H = 6;
const INACTIVE_W = 32;
const ACTIVE_W = 56;
const GAP = 8;

/**
 * 1 on the active page, 0 a full page away. A fractional progress blends only
 * the two neighboring dots, for any page count.
 */
export function carouselDotBlend(index: number, progress: number, count: number): number {
  if (count <= 1) return 1;
  const clamped = Math.min(Math.max(progress, 0), count - 1);
  return Math.max(0, 1 - Math.abs(clamped - index));
}

export interface CarouselDotIndicatorProps {
  /** Total number of pages/slides. */
  count: number;
  /** Active position — 0-based. Fractional values animate mid-swipe. */
  progress: number;
  /** Optional labels for each dot (used in aria-label). */
  labels?: string[];
  /** Accessible name for the row of dots. */
  ariaLabel?: string;
  /** Disables transition so dots track live drag position. */
  isDragging?: boolean;
  onSelect?: (index: number) => void;
}

export function CarouselDotIndicator({
  count,
  progress,
  labels,
  ariaLabel = 'Panel pages',
  isDragging = false,
  onSelect,
}: CarouselDotIndicatorProps) {
  const dots = useRecipe({ recipe: carouselDotsRecipe });
  const reduceMotion = useReducedMotion();
  const clampedProgress = Math.min(Math.max(progress, 0), Math.max(0, count - 1));

  const transition = reduceMotion || isDragging
    ? { duration: 0 }
    : { duration: 0.26, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] };

  if (count <= 1) return null;

  return (
    <HStack
      justify="center"
      gap={`${GAP}px`}
      role="tablist"
      aria-label={ariaLabel}
      css={dots()}
      pointerEvents={onSelect ? 'auto' : 'none'}
    >
      {Array.from({ length: count }, (_, index) => {
        const label = labels?.[index] ?? `Page ${index + 1}`;
        const blend = carouselDotBlend(index, clampedProgress, count);
        const width = INACTIVE_W + (ACTIVE_W - INACTIVE_W) * blend;
        const bg = `color-mix(in srgb, var(--chakra-colors-accent) ${Math.round(blend * 100)}%, var(--chakra-colors-border))`;

        return (
          <Box
            key={index}
            as={onSelect ? 'button' : 'span'}
            {...(onSelect ? { type: 'button' as const } : {})}
            role="tab"
            aria-label={label}
            aria-selected={Math.round(clampedProgress) === index}
            onClick={onSelect ? () => onSelect(index) : undefined}
            p={0}
            border="none"
            bg="transparent"
            cursor={onSelect ? 'pointer' : 'default'}
            lineHeight={0}
            css={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <motion.div
              animate={{ width, backgroundColor: bg }}
              initial={false}
              transition={transition}
              style={{
                height: DOT_H,
                borderRadius: 999,
              }}
            />
          </Box>
        );
      })}
    </HStack>
  );
}
